"use client";

import React from "react";

import Image from "next/image";
import DecorationLine from "@/components/global/ui/DecorationLine";
import Chip from "@/components/global/ui/Chip";
import UnicornEmbedWrapper from "@/components/global/utils/UnicornEmbedWrapper";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {GROQCornerButton, GROQImage} from "@/sanity/lib/definitions";
import {PortableTextBlock} from "sanity";
import {CTACornerButton} from "@/shadcn/ui/corner-button";

import bastiImage from "@/public/home-new/audienceImage.jpg";

export default function TargetAudienceSection({
  chip,
  title,
  items,
  cta,
  className
}: {
  chip: {
    icon: GROQImage;
    text: string;
  };
  title: PortableTextBlock[];
  items: {
    icon: GROQImage;
    title: string;
    text: PortableTextBlock[];
  }[];
  cta?: GROQCornerButton;
  className?: string;
}) {
  return (
    <section className={cn(
      "relative overflow-clip pt-[13rem] lg:pt-[24rem]",
      className
    )}>
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />

      <div className="container relative flex flex-col items-center">
        <div className="w-[min(187.75rem,100%)] flex flex-col items-start">
          <Chip
            image={chip?.icon}
            text={chip?.text}
          />

          {title && (
            <div className="richtext richtext-start mb-[3rem] lg:mb-[4rem]">
              <PortableText
                value={title}
                components={richTextComponent}
              />
            </div>
          )}

          <div className="w-full flex flex-col lg:flex-row items-center lg:items-start lg:justify-between gap-[3rem]">
            <div className="w-full lg:w-auto lg:grow relative aspect-[1644/960]">
              <UnicornEmbedWrapper
                assetID="wLT440YudR7pBIpbpUZX"
                placeholderImage={bastiImage}
                placeholderCover={true}
              />

              <div className="absolute inset-0 p-[2rem] flex flex-col items-end justify-center">
                <p className="bg-[#D9F9B9] px-[.5rem] py-[.25rem] mb-[1.5rem] leading-none text-[2rem] text-black font-geistmono font-medium uppercase">Bastian K.</p>
                <p className="mb-0 leading-[1.2] text-[1.75rem] text-white font-geistmono font-medium uppercase text-end">Trading Typ<br/>/ Profis</p>
              </div>
            </div>

            {items && (
              <div className="targetAudienceList w-full lg:w-[50rem] flex flex-col items-center lg:items-start flex-shrink-0">
                <div className={cn(
                  "grid grid-cols-1 lg:grid-cols-1 gap-y-[3rem] lg:gap-y-[5.5rem] gap-x-[2rem]",
                  items.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"
                )}>
                  {items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-[2rem] sm:gap-[1rem] lg:gap-[2rem]"
                    >
                      {item.icon && (
                        <Image
                          className="!w-[3rem] flex-shrink-0"
                          src={item.icon}
                          alt="list title icon"
                        />
                      )}
                      <div className="flex flex-col items-start gap-[1.25rem]">
                        <h3 className="!mb-0 !leading-[3rem] !font-geist !text-[2.5rem] !text-black/80">{item?.title}</h3>

                        {item?.text && (
                          <div className="richtext richtext-start prose-li:text-black/80 prose-li:text-[2.25rem] [&_::marker]:text-black/70 prose-li:p-0 prose-li:m-0 prose-ul:flex prose-ul:flex-col prose-ul:gap-[.25rem] prose-ul:pl-[2.5rem]">
                            <PortableText
                              value={item.text}
                              components={richTextComponent}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {cta && (
                  <CTACornerButton
                    className="mt-[4rem]"
                    data={cta}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
